import { mainApi } from '../index';
import { buildAppointmentFilters } from '../../utils/appointmentFilters';
import { getLocalTimezone } from '../../helpers';
import type { AppointmentFilters, BlockedTimesResponse, BlockTimeRequest, BookingsResponse } from './types';
import type { AvailabilityResponse } from './availabilityTypes';

export type {
    AppointmentFilters,
    BlockedTimeEvent,
    BlockedTimesResponse,
    Booking,
    BookingAttendee,
    BookingsResponse,
    BlockTimeRequest,
} from './types';

export type { AvailabilityResponse, AvailableSlot } from './availabilityTypes';

export const appointmentsApi = {
    // Fetch blocked times
    getBlockedTimes: async (timezone: string, startDate: string, endDate: string): Promise<BlockedTimesResponse> => {
        const params = new URLSearchParams({
            timezone,
            start: startDate,
            end: endDate,
        });

        const url = `/v1/booking/blocked-time?${params}`;

        try {
            const response = await mainApi.get(url);
            return response.data;
        } catch (error) {
            console.error('🌐 API Error - Blocked Times:', error);
            throw error;
        }
    },

    // Fetch bookings/appointments
    getBookings: async (filters: AppointmentFilters): Promise<BookingsResponse> => {
        const filterArray = buildAppointmentFilters(filters);

        const params = new URLSearchParams({
            timezone: filters.timezone || getLocalTimezone(),
            filter_array: JSON.stringify(filterArray),
            page: String(filters.page || 1),
            pageSize: String(filters.pageSize || 10),
            keyword: '', // Empty keyword for now
        });

        const url = `/v1/booking?${params}`;

        try {
            const response = await mainApi.get(url);
            return response.data;
        } catch (error) {
            console.error('🌐 API Error - Bookings:', error);
            throw error;
        }
    },

    // Fetch single booking by ID
    getBookingById: async (bookingId: string, timezone: string = getLocalTimezone()): Promise<BookingsResponse> => {
        const params = new URLSearchParams({
            timezone,
            page: '1',
            pageSize: '1',
            bookingId,
            keyword: '',
            filter_array: '[]',
        });

        const url = `/v1/booking?${params}`;

        try {
            const response = await mainApi.get(url);
            return response.data;
        } catch (error) {
            console.error('🌐 API Error - Single Booking:', error);
            throw error;
        }
    },

    // Block time (create or edit)
    blockTime: async (blockTimeData: BlockTimeRequest): Promise<void> => {
        const url = `/v1/booking/blocked-time`;

        try {
            await mainApi.post(url, blockTimeData);
        } catch (error) {
            console.error('🌐 API Error - Block Time:', error);
            throw error;
        }
    },

    // Delete blocked time
    deleteBlockedTime: async (blockedTimeId: string): Promise<void> => {
        const url = `/v1/booking/blocked-time/${blockedTimeId}`;

        try {
            await mainApi.delete(url);
        } catch (error) {
            console.error('🌐 API Error - Delete Blocked Time:', error);
            throw error;
        }
    },

    // Reschedule appointment
    rescheduleAppointment: async (
        bookingId: string,
        rescheduleData: {
            timezone: string;
            date: string;
            start: string;
            end: string;
            reschedule_reason: string;
        }
    ): Promise<void> => {
        const url = `/v1/booking/reschedule/${bookingId}`;
        await mainApi.post(url, rescheduleData);
    },

    // Cancel appointment
    cancelAppointment: async (
        bookingId: string,
        cancelData: {
            timezone: string;
            reason: string;
        }
    ): Promise<void> => {
        const url = `/v1/booking/cancel/${bookingId}`;
        await mainApi.post(url, cancelData);
    },

    // Get availability for rescheduling
    getAvailability: async (
        bookingId: string,
        startDate: string,
        endDate: string,
        timezone: string = getLocalTimezone()
    ): Promise<AvailabilityResponse> => {
        const params = new URLSearchParams({
            start: startDate,
            end: endDate,
            timezone,
        });

        const url = `/v1/booking/availability/${bookingId}?${params}`;
        const response = await mainApi.get(url);
        return response.data;
    },
};
